import React from 'react';
import { connect } from 'react-redux';
import { i18n } from '@lingui/core';
import {
    onChangeCompetitorsFleetTransfersDateMode,
    onChangeCompetitorsFleetTransfersSelectedDate,
    onChangeMapFiltersMulti,
} from '../../ac/onChangeMapFilters';
import { functionalAreasYandexUrentTypes } from '../../constants/variables';
import { withDisplayedLayersCheck } from '../../HOC/withDisplayedLayersCheck';
import { MapTimeline } from './MapTimeline';
import { dayNum, isoFromDay, yearMonthScale } from './presets';

/**
 * Привязка MapTimeline к слою «Переброски флота (конкуренты)».
 *
 * Даты-наблюдения и цвета операторов берём из mapFilters.competitorsFleetTransfers-
 * AvailableDates/DateOperators (массив 'YYYY-MM-DD' → операторы, грузится асинхронно
 * из тайлов, Map.jsx). Оба режима периода (mapFilters.competitorsFleetTransfersDateMode)
 * используют стандартную годовую шкалу с разбивкой по месяцам (пресет yearMonthScale,
 * как в слое «Фактический ребаланс» / режим «Сутки»): рельса скоупится на выбранный
 * год (степпер года в шапке), доступные даты помечены штрихами-событиями (eventTicks).
 *  - false — «Выбрать диапазон»: два бегунка (range-brush) в пределах года; границы —
 *    competitorsFleetTransfersRangeFrom/RangeTo.
 *  - true  — «Выбрать дату»: курсор на конкретную дату года; competitorsFleetTransfersSelectedDate.
 *
 * Известное ограничение: операторы и даты собираются из тайлов (querySourceFeatures) и
 * неполны по текущему bbox/zoom — как и в сайдбаре.
 *
 * Монтируется только когда слой активен (withDisplayedLayersCheck).
 */

// 'YYYY-MM-DD' → 'DD.MM.YYYY' (заголовок)
const formatFull = iso => {
    if (!iso) return '—';
    const [ y, m, d ] = iso.split('-');
    return `${d}.${m}.${y}`;
};
const yearOf = iso => iso.slice(0, 4);
// Оператор → цвет (тот же источник, что у заливки слоя); фолбэк — серый
const OPERATOR_COLORS = functionalAreasYandexUrentTypes.reduce((acc, { value, fill }) => {
    acc[value] = fill;
    return acc;
}, {});
const operatorColor = op => OPERATOR_COLORS[op] || '#808080';
const FleetTransfersTimelineComponent = ({
    dateMode,
    selectedDate,
    availableDates,
    dateOperators,
    rangeFrom,
    rangeTo,
    setDateMode,
    setSelectedDate,
    setRange,
}) => {
    const dates = React.useMemo(() => availableDates || [], [ availableDates ]);
    const ops = React.useMemo(() => dateOperators || {}, [ dateOperators ]);
    // Годы, за которые есть данные (навигация степпером).
    const years = React.useMemo(
        () => Array.from(new Set(dates.map(yearOf))).sort(),
        [ dates ]
    );
    // Выбранный год: локальная навигация. Дефолт — год выбранной даты/начала диапазона,
    // иначе последний доступный год.
    const [ year, setYear ] = React.useState(null);
    const refDate = dateMode ? selectedDate : rangeFrom;
    const effectiveYear = year && years.includes(year)
        ? year
        : ((refDate && yearOf(refDate)) || years[years.length - 1] || null);
    // Стандартная годовая шкала с разбивкой по месяцам (пресет).
    const { domain, weekTicks, yearMinDay, yearMaxDay } = React.useMemo(
        () => yearMonthScale(Number(effectiveYear), i18n.locale),
        [ effectiveYear ]
    );
    // Штрихи-события: доступные даты выбранного года (позиция — номер дня).
    const yearDates = React.useMemo(
        () => dates.filter(d => yearOf(d) === effectiveYear),
        [ dates, effectiveYear ]
    );
    const eventTicks = React.useMemo(
        () => yearDates
            .filter(d => ops[d] && ops[d].length > 0)
            .map(d => ({
                t: dayNum(d),
                colors: ops[d].map(operatorColor),
                label: `${formatFull(d)}: ${ops[d].join(', ')}`,
            })),
        [ yearDates, ops ]
    );
    // Степпер года (общий для обоих режимов).
    const yearIdx = years.indexOf(effectiveYear);
    const stepperControl = years.length > 0 ? {
        display: effectiveYear || '—',
        onPrev: () => yearIdx > 0 && setYear(years[yearIdx - 1]),
        onNext: () => yearIdx < years.length - 1 && setYear(years[yearIdx + 1]),
        prevDisabled: yearIdx <= 0,
        nextDisabled: yearIdx < 0 || yearIdx >= years.length - 1,
        // onValueClick не задан — год меняется только стрелками
    } : null;

    // Без загруженных дат таймлайн бессмысленен
    if (dates.length === 0 || !effectiveYear) return null;

    // ─── Режим «Выбрать диапазон»: два бегунка в пределах года ───
    if (!dateMode) {
        let fromDay = dayNum(rangeFrom);
        let toDay = dayNum(rangeTo);
        if (fromDay === null || fromDay < yearMinDay || fromDay > yearMaxDay) fromDay = yearMinDay;
        if (toDay === null || toDay < yearMinDay || toDay > yearMaxDay) toDay = yearMaxDay;
        if (fromDay > toDay) fromDay = toDay;
        const onRangeChange = ([ a, b ]) => {
            const fromIso = isoFromDay(Math.round(a));
            const toIso = isoFromDay(Math.round(b));
            if (fromIso !== rangeFrom || toIso !== rangeTo) {
                setRange({ competitorsFleetTransfersRangeFrom: fromIso, competitorsFleetTransfersRangeTo: toIso });
            }
        };
        return (
            <MapTimeline
                domain={domain}
                range={[ fromDay, toDay ]}
                onRangeChange={onRangeChange}
                eventTicks={eventTicks}
                minorTicks={weekTicks}
                stepperControl={stepperControl}
            />
        );
    }

    // ─── Режим «Выбрать дату»: курсор на конкретную дату года ───
    let cursorDay = dayNum(selectedDate);
    if (cursorDay === null || cursorDay < yearMinDay || cursorDay > yearMaxDay) {
        // дефолт — последняя доступная дата года, иначе конец года
        const last = yearDates.length ? dayNum(yearDates[yearDates.length - 1]) : yearMaxDay;
        cursorDay = last;
    }
    const onCursorChange = n => {
        const iso = isoFromDay(Math.round(n));
        if (!dateMode) setDateMode(true);
        if (iso !== selectedDate) setSelectedDate(iso);
    };
    return (
        <MapTimeline
            domain={domain}
            cursor={cursorDay}
            onCursorChange={onCursorChange}
            eventTicks={eventTicks}
            minorTicks={weekTicks}
            stepperControl={stepperControl}
        />
    );
};
const mapStateToProps = ({ mapFilters }) => ({
    dateMode: mapFilters.competitorsFleetTransfersDateMode,
    selectedDate: mapFilters.competitorsFleetTransfersSelectedDate,
    availableDates: mapFilters.competitorsFleetTransfersAvailableDates,
    dateOperators: mapFilters.competitorsFleetTransfersDateOperators,
    rangeFrom: mapFilters.competitorsFleetTransfersRangeFrom,
    rangeTo: mapFilters.competitorsFleetTransfersRangeTo,
});
const mapDispatchToProps = {
    setDateMode: onChangeCompetitorsFleetTransfersDateMode,
    setSelectedDate: onChangeCompetitorsFleetTransfersSelectedDate,
    setRange: onChangeMapFiltersMulti,
};
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(FleetTransfersTimelineComponent);

export const FleetTransfersTimeline = withDisplayedLayersCheck(
    ConnectedComponent,
    'Переброски флота (конкуренты)'
);
